/**
 * Persistent Logger - Sistema de logs que sobrevive a restarts do app iOS
 * Usa Capacitor Preferences para persistência nativa cross-process
 */

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { logger } from './logger';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';
export type LogCategory = 
  | 'BLE'           // Monitor cardíaco Bluetooth
  | 'SPOTIFY'       // Integração Spotify
  | 'GPS'           // Tracking de corrida
  | 'AUTH'          // Autenticação
  | 'NETWORK'       // Status de rede
  | 'APP'           // Geral do app
  | 'OAUTH'         // Fluxos OAuth
  | 'HEALTHKIT'     // Apple Health
  | 'GOOGLEFIT'     // Google Fit
  | 'PUSH'          // Push Notifications
  | 'WEARABLE'      // Garmin/Polar/Suunto
  | 'MUSIC'         // Playback musical
  | 'STORAGE';      // Armazenamento/Cache

export interface PersistentLog {
  id: string;
  timestamp: number;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: any;
  correlationId: string;
  userAgent: string;
  platform: string;
  synced?: boolean;
  syncedAt?: number;
}

interface LogFilter {
  category?: LogCategory;
  level?: LogLevel;
  startTime?: number;
  endTime?: number;
}

const MAX_LOGS = 500; // Limite de logs armazenados
const STORAGE_KEY = 'ybr_persistent_logs';
const SYNC_INTERVAL = 30000; // 30 segundos
const MIN_LOGS_TO_SYNC = 5; // Sincronizar quando tiver pelo menos 5 logs

class PersistentLogger {
  private logs: PersistentLog[] = [];
  private isInitialized = false;
  private syncIntervalId?: NodeJS.Timeout;
  private isSyncing = false;

  constructor() {
    this.initialize();
    this.setupGlobalErrorHandlers();
    this.startAutoSync();
  }

  private async initialize() {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (value) {
        this.logs = JSON.parse(value);
        logger.info('PersistentLogger initialized', { 
          logsCount: this.logs.length,
          unsyncedCount: this.logs.filter(l => !l.synced).length,
          platform: Capacitor.getPlatform() 
        });
      }
      this.isInitialized = true;
    } catch (error) {
      logger.error('Failed to initialize PersistentLogger', error);
      this.logs = [];
      this.isInitialized = true;
    }
  }

  /**
   * Inicia sincronização automática com o backend
   */
  private startAutoSync() {
    // Sync a cada 30 segundos
    this.syncIntervalId = setInterval(() => {
      this.syncToBackend().catch(err => 
        logger.error('Auto-sync failed', err)
      );
    }, SYNC_INTERVAL);

    // Sync inicial após 5 segundos
    setTimeout(() => {
      this.syncToBackend().catch(err =>
        logger.error('Initial sync failed', err)
      );
    }, 5000);
  }

  /**
   * Para a sincronização automática
   */
  public stopAutoSync() {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = undefined;
    }
  }

  /**
   * Sincroniza logs não enviados com o backend
   */
  public async syncToBackend(retryCount = 0): Promise<{ success: boolean; syncedCount: number; error?: string }> {
    if (this.isSyncing) {
      return { success: false, syncedCount: 0, error: 'Sync already in progress' };
    }

    if (!this.isInitialized) {
      return { success: false, syncedCount: 0, error: 'Logger not initialized' };
    }

    const unsyncedLogs = this.logs.filter(l => !l.synced);
    
    if (unsyncedLogs.length === 0) {
      return { success: true, syncedCount: 0 };
    }

    const hasErrors = unsyncedLogs.some(l => l.level === 'error' || l.level === 'fatal');
    if (unsyncedLogs.length < MIN_LOGS_TO_SYNC && !hasErrors) {
      return { success: true, syncedCount: 0 };
    }

    this.isSyncing = true;

    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        logger.warn('No active session, skipping log sync');
        this.isSyncing = false;
        return { success: false, syncedCount: 0, error: 'No active session' };
      }

      const deviceInfo = this.getDeviceInfo();

      const { data, error } = await supabase.functions.invoke('sync-app-logs', {
        body: {
          logs: unsyncedLogs,
          deviceInfo
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success && data?.syncedIds) {
        const syncedIds = new Set(data.syncedIds);
        const now = Date.now();
        
        this.logs = this.logs.map(log => 
          syncedIds.has(log.id) 
            ? { ...log, synced: true, syncedAt: now }
            : log
        );

        await this.saveLogs();
        logger.info('Logs synced successfully', { count: data.syncedCount });
        return { success: true, syncedCount: data.syncedCount };
      }

      return { success: false, syncedCount: 0, error: 'Invalid response from backend' };
    } catch (error: any) {
      logger.error('Failed to sync logs to backend', error);
      
      // Retry once after 3 seconds for network errors
      if (retryCount === 0 && (error.message?.includes('fetch') || error.message?.includes('network'))) {
        logger.info('Retrying log sync after 3s', { retryCount: retryCount + 1 });
        this.isSyncing = false;
        await new Promise(resolve => setTimeout(resolve, 3000));
        return this.syncToBackend(retryCount + 1);
      }
      
      return { success: false, syncedCount: 0, error: error.message };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Retorna contagem de logs não sincronizados
   */
  public getUnsyncedCount(): number {
    return this.logs.filter(l => !l.synced).length;
  }

  /**
   * Retorna status de sincronização
   */
  public getSyncStatus(): { isSyncing: boolean; unsyncedCount: number; lastSyncTime?: number } {
    const syncedLogs = this.logs.filter(l => l.synced);
    const lastSyncTime = syncedLogs.length > 0 
      ? Math.max(...syncedLogs.map(l => l.syncedAt || 0))
      : undefined;

    return {
      isSyncing: this.isSyncing,
      unsyncedCount: this.getUnsyncedCount(),
      lastSyncTime
    };
  }

  private async saveLogs() {
    try {
      // Rotação: manter apenas os últimos MAX_LOGS logs
      if (this.logs.length > MAX_LOGS) {
        this.logs = this.logs.slice(-MAX_LOGS);
      }

      await Preferences.set({
        key: STORAGE_KEY,
        value: JSON.stringify(this.logs)
      });
    } catch (error) {
      logger.error('Failed to save logs to Preferences', error);
    }
  }

  private setupGlobalErrorHandlers() {
    // Capturar erros não tratados
    if (typeof window !== 'undefined') {
      window.onerror = (message, source, lineno, colno, error) => {
        this.addLog('APP', 'fatal', 'Uncaught error', {
          message: String(message),
          source,
          lineno,
          colno,
          error: error?.toString(),
          stack: error?.stack
        });
        return false;
      };

      // Capturar rejeições de promessas não tratadas
      window.onunhandledrejection = (event) => {
        this.addLog('APP', 'error', 'Unhandled promise rejection', {
          reason: event.reason?.toString(),
          stack: event.reason?.stack
        });
      };
    }
  }

  /**
   * Adiciona um log ao sistema persistente
   */
  public addLog(
    category: LogCategory,
    level: LogLevel,
    message: string,
    data?: any
  ) {
    if (!this.isInitialized) {
      // Buffer logs até inicializar
      setTimeout(() => this.addLog(category, level, message, data), 100);
      return;
    }

    const log: PersistentLog = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      level,
      category,
      message,
      data: data || undefined,
      correlationId: logger.getCorrelationId(),
      userAgent: navigator.userAgent,
      platform: Capacitor.getPlatform()
    };

    this.logs.push(log);

    // Salvar de forma assíncrona (não bloquear execução)
    this.saveLogs().catch(err => 
      logger.error('Failed to save log', err)
    );

    // Sync imediato para erros críticos
    if (level === 'error' || level === 'fatal') {
      this.syncToBackend().catch(err =>
        logger.error('Failed to sync error log', err)
      );
    }

    // Também logar no console/logger tradicional
    const logMethod = level === 'fatal' || level === 'error' ? 'error' 
      : level === 'warn' ? 'warn' 
      : 'info';
    
    logger[logMethod](`[${category}] ${message}`, data);
  }

  /**
   * Retorna logs filtrados
   */
  public getLogs(filter?: LogFilter): PersistentLog[] {
    let filtered = [...this.logs];

    if (filter?.category) {
      filtered = filtered.filter(log => log.category === filter.category);
    }

    if (filter?.level) {
      filtered = filtered.filter(log => log.level === filter.level);
    }

    if (filter?.startTime) {
      filtered = filtered.filter(log => log.timestamp >= filter.startTime!);
    }

    if (filter?.endTime) {
      filtered = filtered.filter(log => log.timestamp <= filter.endTime!);
    }

    return filtered.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Exporta logs em formato texto para compartilhar
   */
  public exportLogs(filter?: LogFilter): string {
    const logs = this.getLogs(filter);
    const now = new Date();
    const deviceInfo = this.getDeviceInfo();

    let output = '=== YOU BETTER RUN - LOGS DO DISPOSITIVO ===\n';
    output += `Exportado em: ${now.toLocaleString('pt-BR')}\n`;
    output += `Dispositivo: ${deviceInfo.device}\n`;
    output += `Plataforma: ${deviceInfo.platform}\n`;
    output += `User Agent: ${deviceInfo.userAgent}\n`;
    output += `\n--- LOGS (${logs.length} entradas) ---\n\n`;

    logs.forEach(log => {
      const time = new Date(log.timestamp).toLocaleTimeString('pt-BR');
      output += `[${time}] ${log.level.toUpperCase()} | ${log.category} | ${log.message}\n`;
      
      if (log.data) {
        try {
          const dataStr = JSON.stringify(log.data, null, 2);
          output += `  → ${dataStr}\n`;
        } catch {
          output += `  → ${String(log.data)}\n`;
        }
      }
      
      output += '\n';
    });

    return output;
  }

  /**
   * Limpa todos os logs
   */
  public async clearLogs() {
    this.logs = [];
    await Preferences.remove({ key: STORAGE_KEY });
    logger.info('All logs cleared');
  }

  /**
   * Retorna informações do dispositivo
   */
  private getDeviceInfo() {
    const platform = Capacitor.getPlatform();
    const userAgent = navigator.userAgent;
    
    // Tentar extrair modelo do device do userAgent
    let device = 'Unknown';
    if (userAgent.includes('iPhone')) {
      const match = userAgent.match(/iPhone OS (\d+_\d+)/);
      device = match ? `iPhone (iOS ${match[1].replace('_', '.')})` : 'iPhone';
    } else if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android (\d+\.?\d*)/);
      device = match ? `Android ${match[1]}` : 'Android';
    } else {
      device = platform === 'web' ? 'Web Browser' : platform;
    }

    return { device, platform, userAgent };
  }

  /**
   * Retorna estatísticas dos logs
   */
  public getStats() {
    const total = this.logs.length;
    const byCategory: Partial<Record<LogCategory, number>> = {
      BLE: 0,
      SPOTIFY: 0,
      GPS: 0,
      AUTH: 0,
      NETWORK: 0,
      APP: 0,
      OAUTH: 0,
      HEALTHKIT: 0,
      GOOGLEFIT: 0,
      PUSH: 0,
      WEARABLE: 0,
      MUSIC: 0,
      STORAGE: 0
    };
    const byLevel: Record<LogLevel, number> = {
      debug: 0,
      info: 0,
      warn: 0,
      error: 0,
      fatal: 0
    };

    this.logs.forEach(log => {
      byCategory[log.category] = (byCategory[log.category] || 0) + 1;
      byLevel[log.level] = (byLevel[log.level] || 0) + 1;
    });

    return { total, byCategory, byLevel };
  }
}

// Singleton instance
export const persistentLogger = new PersistentLogger();
